import { useState } from "react"
import { router } from "@inertiajs/react"
import { ArrowRight } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import type { CalendarChannel } from "./types"

type Props = {
  channels: CalendarChannel[]
  trigger: React.ReactNode
}

export function BulkPushBackModal({ channels, trigger }: Props) {
  const [open, setOpen] = useState(false)
  const [channel, setChannel] = useState<string>(channels[0]?.slug || "")
  const [fromDate, setFromDate] = useState<string>(todayIso())
  const [submitting, setSubmitting] = useState(false)

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!channel) return
    setSubmitting(true)
    router.post(`/calendar/push_back/${channel}`, { from_date: fromDate }, {
      onFinish: () => setSubmitting(false),
      onSuccess: () => setOpen(false),
    })
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Bulk push-back</DialogTitle>
          <DialogDescription>
            Move every scheduled piece in a channel on or after a date to the next open slot.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="mt-3 flex flex-col gap-3">
          <div>
            <label htmlFor="push-back-channel">Channel</label>
            <Select id="push-back-channel" value={channel} onChange={(e) => setChannel(e.target.value)}>
              {channels.map((c) => (
                <option key={c.slug} value={c.slug}>{c.name}</option>
              ))}
            </Select>
          </div>

          <div>
            <label htmlFor="push-back-date">Push back from</label>
            <Input
              id="push-back-date"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              required
            />
          </div>

          <Button type="submit" disabled={submitting || !channel}>
            <ArrowRight className="h-4 w-4" />
            {submitting ? "Pushing back…" : "Push back"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function todayIso() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
}
